/**
 * Custom Tag Creator Component
 */
import React, { useState } from 'react';
import { QrCode } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomTagForm = ({ batches = [], onCreate }) => {
  const [customTagCode, setCustomTagCode] = useState('');
  const [customTagDescription, setCustomTagDescription] = useState('');
  const [customTagBatch, setCustomTagBatch] = useState('');
  const [qrEnabled, setQrEnabled] = useState(true);
  const [creatingTag, setCreatingTag] = useState(false);

  const createCustomTag = async () => {
    if (!customTagCode.trim()) {
      toast.error('Tag code is required');
      return;
    }
    
    setCreatingTag(true);
    try {
      await axios.post(`${API}/admin/create-tag`, {
        nfc_code: customTagCode.trim(),
        admin_notes: customTagDescription,
        batch: customTagBatch || 'custom',
        qr_enabled: qrEnabled
      });
      toast.success(`Tag created successfully${!qrEnabled ? ' (QR disabled)' : ''}`);
      setCustomTagCode('');
      setCustomTagDescription('');
      setCustomTagBatch('');
      setQrEnabled(true);
      if (onCreate) onCreate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="rounded-3xl border border-border/40 bg-card/50 p-8">
        <h2 className="text-2xl font-bold mb-6">Create Custom Tag</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tag Code *</label>
            <input
              type="text"
              value={customTagCode}
              onChange={(e) => setCustomTagCode(e.target.value.replace(/\s/g, '-'))}
              placeholder="e.g., my-wedding-2024"
              className="w-full bg-secondary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="custom-tag-code-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              No spaces allowed. Will be used in URLs.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description / Admin Notes</label>
            <input
              type="text"
              value={customTagDescription}
              onChange={(e) => setCustomTagDescription(e.target.value)}
              placeholder="Internal notes about this tag"
              className="w-full bg-secondary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="custom-tag-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Batch / Folder</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={customTagBatch}
                onChange={(e) => setCustomTagBatch(e.target.value.replace(/\s/g, '-'))}
                placeholder="e.g., weddings"
                className="flex-1 bg-secondary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                list="batch-suggestions"
                data-testid="custom-tag-batch-input"
              />
              <datalist id="batch-suggestions">
                {batches.map(b => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty for 'custom' batch
            </p>
          </div>
          
          {/* QR Code Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/50 border border-border/50">
            <div className="flex items-center gap-3">
              <QrCode className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Enable QR Code</p>
                <p className="text-xs text-muted-foreground">Allow user to download QR code for this tag</p>
              </div>
            </div>
            <button
              onClick={() => setQrEnabled(!qrEnabled)}
              className={`w-12 h-6 rounded-full transition-all ${
                qrEnabled ? 'bg-primary' : 'bg-secondary border border-border'
              }`}
              data-testid="custom-tag-qr-toggle"
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                qrEnabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          
          <button
            onClick={createCustomTag}
            disabled={creatingTag || !customTagCode.trim()}
            className="w-full bg-primary text-primary-foreground rounded-full py-3 font-medium hover:scale-105 transition-all disabled:opacity-50"
            data-testid="create-custom-tag-btn"
          >
            {creatingTag ? 'Creating...' : 'Create Tag'}
          </button>
        </div>

        {/* Preview */}
        {customTagCode && (
          <div className="mt-6 p-4 bg-secondary/30 rounded-xl">
            <p className="text-sm font-medium mb-2">Preview:</p>
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">Upload URL:</span>{' '}
                <code className="bg-background px-2 py-0.5 rounded">/{customTagCode}/tag</code>
              </p>
              <p>
                <span className="text-muted-foreground">View URL:</span>{' '}
                <code className="bg-background px-2 py-0.5 rounded">/{customTagCode}</code>
              </p>
              <p>
                <span className="text-muted-foreground">QR Code:</span>{' '}
                <span className={qrEnabled ? 'text-green-600' : 'text-orange-600'}>
                  {qrEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTagForm;
